import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useField } from 'react-final-form';
import { Grid, FlexRow, Styles, Form, FlexBaseCol, Icon } from '@contently/toolkit';
import ReactPlaceholder from 'react-placeholder';
import useFetchingField from '../utils/hooks/useFetchingField';

const { ErrorWrapper } = Form;

const parseSkills = res => res.data.map(skill => skill.group);

const defaultSkills = ['filmmakers', 'designers', 'photographers', 'writers'];

export const useFetchingFieldSkills = () => {
  return useFetchingField('/recommended_contributors/skill_groups', defaultSkills, parseSkills);
};

const SkillWrapper = styled('div')`
  text-align: center;
  color: #a2a2a2;
  font-weight: 400;
  font-size: 12px;
  margin: 0 5px 15px;
  ${props => (props.selected ? `color: ${Styles.colors.blue.primary};` : null)}
`;

const Skill = styled('div')`
  height: 90px;
  width: 90px;
  background: #fff;
  border: 2px solid ${Styles.colors.grey.secondary};
  border-radius: 30px;
  ${props => (props.selected ? `border-color: ${Styles.colors.blue.primary};` : null)}
  cursor: pointer;
  margin: 0 auto 8px;
  box-shadow: 0px 2px 8px #f7f7f7;
  transition: all 0.3s ease;

  &:hover {
    border-color: ${Styles.colors.blue.primary};
  }

  ${Icon} {
    color: ${Styles.colors.grey.secondary};
    height: 100%;
    width: 100%;
    font-size: 3em;
    transition: all 0.3s ease;
    ${props => (props.selected ? `color: ${Styles.colors.blue.primary};` : null)}
  }
`;

const SkillPlaceholder = styled(Skill)`
  background-color: ${Styles.colors.grey.secondary};
  border: 0;
  animation: ${Styles.pulse} ${Styles.animate.pulse} infinite;
`;

const SkillColWrapper = styled(FlexBaseCol)`
  @media screen and (max-width: 900px) {
    flex-basis: 50%;
  }
  @media screen and (max-width: 639px) {
    flex-basis: auto;
  }
  @media screen and (max-width: 500px) {
    flex-basis: 50%;
  }
`;

const skillIcons = {
  filmmakers: 'video',
  designers: 'image',
  photographers: 'photo',
  writers: 'book'
};

const capitalize = str => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const SkillField = ({ name, skills, fetching, validate }) => {
  const { input, meta } = useField(name, { validate });

  return (
    <Grid>
      <ErrorWrapper touched={meta.touched} error={meta.error}>
        <FlexRow>
          {skills.map(skill => (
            <SkillColWrapper key={skill}>
              <ReactPlaceholder
                showLoadingAnimation
                customPlaceholder={<SkillPlaceholder />}
                ready={!fetching}
              >
                <SkillWrapper selected={input.value === skill}>
                  <Skill selected={input.value === skill} onClick={() => input.onChange(skill)}>
                    <Icon type={skillIcons[skill]} fixedWidth={false} />
                  </Skill>
                  {capitalize(skill)}
                </SkillWrapper>
              </ReactPlaceholder>
            </SkillColWrapper>
          ))}
        </FlexRow>
      </ErrorWrapper>
      <input {...input} type="hidden" />
    </Grid>
  );
};

SkillField.propTypes = {
  name: PropTypes.string.isRequired,
  skills: PropTypes.arrayOf(PropTypes.string).isRequired,
  fetching: PropTypes.bool.isRequired,
  validate: PropTypes.func.isRequired
};

export default SkillField;
